import React from 'react';
import { useTranslation } from 'react-i18next';
import { getPreloaded } from './contentPreload';

const resolveFile = (entry, lang) => {
  const translated = lang !== 'en' && (entry.locales || []).includes(lang);
  return { file: translated ? entry.file.replace(/\.md$/, `.${lang}.md`) : entry.file, translated };
};

// Loads a markdown document in the active language when a translated
// sibling (<name>.<lang>.md, recorded in the manifest's `locales`) exists,
// falling back to English otherwise. `fallback` is true when the visitor's
// language has no translation yet — callers show a small notice.
//
// Checks contentPreload's synchronous cache first (populated server-side
// before renderToString, and client-side from window.__PRELOADED_CONTENT__
// before hydration) so the initial render has real content instead of a
// loading state — useEffect below never runs during SSR, so without this
// every prerendered page would ship empty.
export const useLocalizedBody = (entry, loadBody) => {
  const { i18n } = useTranslation();
  // i18n.language (the detected request), not resolvedLanguage — the latter
  // reads 'en' until the lazy UI bundle registers, which races page mount.
  const lang = (i18n.language || 'en').split('-')[0];
  const [state, setState] = React.useState(() => {
    if (!entry)
      return { body: null, fallback: false };
    const { file, translated } = resolveFile(entry, lang);
    const cached = getPreloaded(file);
    return cached ? { body: cached, fallback: lang !== 'en' && !translated } : { body: null, fallback: false };
  });

  React.useEffect(() => {
    let active = true;
    if (!entry) {
      setState({ body: null, fallback: false });
      return undefined;
    }
    const { file, translated } = resolveFile(entry, lang);
    const cached = getPreloaded(file);
    if (cached) {
      setState({ body: cached, fallback: lang !== 'en' && !translated });
      return undefined;
    }
    setState({ body: null, fallback: false });
    loadBody(file).then(text => active && setState({ body: text, fallback: lang !== 'en' && !translated }));
    return () => { active = false; };
  }, [entry && entry.file, lang]);

  return state;
};
